import { LocationSM } from '@EcamModel/model';
import { CameraEvPermitZone } from '@EcamModel/model/CameraEvPermitZone';

/** Zone option used by the EV permit zone picker (flattened from locations). */
export type EvPermitZoneOption = {
    Id: number;
    PublicName: string;
    LocationId: number;
    LocationName: string;
};

/** Flatten locations -> zone options, keeping location name for grouping/search. */
export const toEvPermitZoneOptions = (locations: LocationSM[]): EvPermitZoneOption[] =>
    locations.flatMap((location) =>
        (location.Zones ?? []).map((zone) => ({
            Id: Number(zone.Id),
            PublicName: zone.PublicName,
            LocationId: Number(location.Id),
            LocationName: location.Name,
        }))
    );

/** Map GET camera EvPermitZones rows to options. */
export const fromCameraEvPermitZones = (rows: CameraEvPermitZone[] = []): EvPermitZoneOption[] =>
    rows.map((row) => ({
        Id: row.ZoneId,
        PublicName: row.Zone?.PublicName ?? `#${row.ZoneId}`,
        LocationId: Number(row.Zone?.LocationId),
        LocationName: row.Zone?.Location?.Name ?? '',
    }));

/** Group zone options by location, keeping first-seen order. */
export const groupZonesByLocation = (zones: EvPermitZoneOption[]) => {
    const groups = new Map<number, { locationName: string; zones: EvPermitZoneOption[] }>();
    zones.forEach((zone) => {
        const group = groups.get(zone.LocationId) ?? { locationName: zone.LocationName, zones: [] };
        group.zones.push(zone);
        groups.set(zone.LocationId, group);
    });
    return Array.from(groups.values());
};

/** 14400 -> "4:00" */
export const formatPermitLengthShort = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}:${String(minutes).padStart(2, '0')}`;
};

/** 14400 -> "4:00 (4 hours)", 5400 -> "1:30 (1 hour 30 minutes)" */
export const formatPermitLength = (seconds?: number | null) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const parts = [
        hours > 0 ? `${hours} ${hours === 1 ? 'hour' : 'hours'}` : '',
        minutes > 0 ? `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}` : '',
    ].filter(Boolean);
    return `${formatPermitLengthShort(seconds)} (${parts.join(' ')})`;
};

const pluralize = (count: number, word: string) => `${count} ${count === 1 ? word : `${word}s`}`;

/** "5 zones · 2 locations" */
export const formatZonesSummary = (zones: EvPermitZoneOption[]) =>
    `${pluralize(zones.length, 'zone')} · ${pluralize(new Set(zones.map((z) => z.LocationId)).size, 'location')}`;
